import { useCallback, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { RateUnitEnum } from "@platform/openapi-types";

import {
  useResolvedRateSchedules,
  useUpdateRate,
  useUpdateProposalRate,
} from "../../../hooks/useRates";
import { formatRateValue } from "../../../utils/rateFormatters";
import {
  getRenewalColumns,
  primarySectionSchedules,
  slotKeyOf,
  type RenewalColumns,
} from "../../../utils/rateSets";
import { rateFieldPathBuilder } from "../../../utils/rateFieldPath";

import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalGroupHeaderCell,
  RenewalHeaderCell,
  RenewalRateCell,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  FONT_SIZE,
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  inputSx,
  gridRowSx,
  gridCellSx,
  headerCellSx,
} from "./constants";

import type { AxiosInstance } from "axios";
import type { RateFieldPathBuilder } from "../../../utils/rateFieldPath";
import type { ColDef } from "ag-grid-community";
import type { FC, JSX } from "react";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";
import type { Rate } from "../../../types";

export interface StandardPepmRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
}

/**
 * Build column definitions for Standard PEPM rate design in RatesGrid
 */
export const buildStandardPepmColumns = (_group: BenefitPlanGroup): ColDef[] =>
  // Standard PEPM doesn't need additional sub-columns within the group column
  // All data is shown in a single cell
  [];

/**
 * Build column definitions for Standard PEPM rate design in SummaryGrid
 * Returns array of ColDef for sub-columns within a benefit plan
 */
export const buildStandardPepmSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] =>
  // Standard PEPM summary uses default column
  [];

// Local grid layouts
// CSS Grid container styling - 2 columns: Label, Value; a renewal column is appended when
// renewal is shown and the table scrolls within the pinned benefit-plan cell.
const singleGridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(90px, 1fr) repeat(2, minmax(80px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : { display: "grid", gridTemplateColumns: "1fr 1fr", width: "100%" };

// Gender split - Label, Male, Female, plus a renewal column per gender when shown.
const genderGridSx = (showRenewal: boolean): Record<string, unknown> => ({
  display: "grid",
  gridTemplateColumns: `minmax(80px, 1fr) repeat(${
    showRenewal ? 4 : 2
  }, minmax(80px, 1fr))`,
  width: "100%",
  ...(showRenewal ? { overflowX: "auto" } : {}),
});

/** Props for single column Standard PEPM layout */
interface StandardPepmSingleProps {
  planId: string;
  totalPepmRate: Rate | undefined;
  onNumberOfEnrolledCommit: (value: string) => void;
  onPepmCommit: (value: string) => void;
  readOnly?: boolean;
  renewal: RenewalColumns;
  fieldPathOf: RateFieldPathBuilder;
}

/** Props for gender split Standard PEPM layout */
interface StandardPepmGenderProps {
  planId: string;
  maleTotalPepmRate: Rate | undefined;
  femaleTotalPepmRate: Rate | undefined;
  onMaleLivesCommit: (value: string) => void;
  onMaleRateCommit: (value: string) => void;
  onFemaleLivesCommit: (value: string) => void;
  onFemaleRateCommit: (value: string) => void;
  readOnly?: boolean;
  renewal: RenewalColumns;
  fieldPathOf: RateFieldPathBuilder;
}

/** Single column Standard PEPM layout component */
const StandardPepmSingleColumn: FC<StandardPepmSingleProps> = ({
  planId,
  totalPepmRate,
  onNumberOfEnrolledCommit,
  onPepmCommit,
  readOnly,
  renewal,
  fieldPathOf,
}): JSX.Element => {
  const showRenewal = renewal.showRenewal;
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";
  return (
    <Box sx={singleGridSx(showRenewal)}>
      {/* Column header row — only needed to name Current vs Renewal */}
      {showRenewal && (
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }} />
          <Box sx={{ ...headerCellSx, justifyContent: "flex-end" }}>
            Current
          </Box>
          <RenewalHeaderCell show={showRenewal} label={renewal.columnLabel} />
        </Box>
      )}

      {/* Row 1: Number of Enrolled */}
      <Box sx={gridRowSx}>
        <Box sx={headerCellSx}>Number of Enrolled</Box>
        <RateReviewCell
          planId={planId}
          fieldPath={fieldPathOf(totalPepmRate, "numberOfLives")}
          sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
        >
          <DebouncedNumericInput
            serverValue={
              totalPepmRate?.numberOfLives != null
                ? String(totalPepmRate.numberOfLives)
                : ""
            }
            onCommit={onNumberOfEnrolledCommit}
            allowDecimals={false}
            sx={inputSx}
            gridId={`standard-pepm-${planId}`}
            cellIndex={0}
            columnsInRow={1}
            readOnly={readOnly}
          />
        </RateReviewCell>
        {showRenewal && <Box sx={{ borderLeft: AG_BORDER }} />}
      </Box>

      {/* Row 2: PEPM */}
      <Box sx={gridRowSx}>
        <Box sx={headerCellSx}>Rate</Box>
        <RateReviewCell
          planId={planId}
          fieldPath={fieldPathOf(totalPepmRate, "rate")}
          sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
        >
          <DebouncedNumericInput
            serverValue={formatRateValue(
              totalPepmRate?.rate,
              totalPepmRate?.unit,
            )}
            onCommit={onPepmCommit}
            allowDecimals
            isCurrency
            sx={inputSx}
            gridId={`standard-pepm-${planId}`}
            cellIndex={0}
            columnsInRow={1}
            readOnly={readOnly}
          />
        </RateReviewCell>
        {showRenewal && totalPepmRate && (
          <RenewalRateCell
            slotKey={slotKeyOf(totalPepmRate)}
            renewal={renewal}
            rowLabel="Rate"
            isLastRow={false}
          />
        )}
      </Box>
    </Box>
  );
};

/** Gender split Standard PEPM layout component */
const StandardPepmGenderSplit: FC<StandardPepmGenderProps> = ({
  planId,
  maleTotalPepmRate,
  femaleTotalPepmRate,
  onMaleLivesCommit,
  onMaleRateCommit,
  onFemaleLivesCommit,
  onFemaleRateCommit,
  readOnly,
  renewal,
  fieldPathOf,
}): JSX.Element => {
  const showRenewal = renewal.showRenewal;
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Rate Design Label Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>PEPM</Box>
      </Box>

      <Box sx={genderGridSx(showRenewal)}>
        {/* Group header row — only when renewal columns are present */}
        {showRenewal && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 2",
                justifyContent: "center",
                fontWeight: 600,
                borderRight: AG_BORDER,
              }}
            >
              Current
            </Box>
            <RenewalGroupHeaderCell
              show={showRenewal}
              label={renewal.columnLabel}
              span={2}
            />
          </Box>
        )}

        {/* Column Headers: Label | Male | Female */}
        <Box sx={gridRowSx}>
          <Box sx={headerCellSx}></Box>
          <Box
            sx={{ ...headerCellSx, justifyContent: "center", fontWeight: 600 }}
          >
            Male
          </Box>
          <Box
            sx={{
              ...headerCellSx,
              justifyContent: "center",
              fontWeight: 600,
              borderRight: currentEndBorderRight,
            }}
          >
            Female
          </Box>
          <RenewalSubHeaderCells
            show={showRenewal}
            labels={["Male", "Female"]}
          />
        </Box>

        {/* Number of Enrolled Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>Number of Enrolled</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(maleTotalPepmRate, "numberOfLives")}
            sx={{ ...gridCellSx }}
          >
            <DebouncedNumericInput
              serverValue={
                maleTotalPepmRate?.numberOfLives != null
                  ? String(maleTotalPepmRate.numberOfLives)
                  : ""
              }
              onCommit={onMaleLivesCommit}
              allowDecimals={false}
              sx={inputSx}
              gridId={`standard-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Male Number of Enrolled" }}
            />
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(femaleTotalPepmRate, "numberOfLives")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={
                femaleTotalPepmRate?.numberOfLives != null
                  ? String(femaleTotalPepmRate.numberOfLives)
                  : ""
              }
              onCommit={onFemaleLivesCommit}
              allowDecimals={false}
              gridId={`standard-pepm-${planId}`}
              cellIndex={1}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Female Number of Enrolled" }}
              sx={inputSx}
            />
          </RateReviewCell>
          {showRenewal && (
            <>
              <Box sx={{ borderLeft: AG_BORDER }} />
              <Box sx={{ borderLeft: AG_BORDER }} />
            </>
          )}
        </Box>

        {/* Rate Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx, borderBottom: "none" }}>Rate</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(maleTotalPepmRate, "rate")}
            sx={{ ...gridCellSx, borderBottom: "none" }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                maleTotalPepmRate?.rate,
                maleTotalPepmRate?.unit,
              )}
              onCommit={onMaleRateCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`standard-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Male Rate" }}
            />
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(femaleTotalPepmRate, "rate")}
            sx={{
              ...gridCellSx,
              borderRight: currentEndBorderRight,
              borderBottom: "none",
            }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                femaleTotalPepmRate?.rate,
                femaleTotalPepmRate?.unit,
              )}
              onCommit={onFemaleRateCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`standard-pepm-${planId}`}
              cellIndex={1}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Female Rate" }}
            />
          </RateReviewCell>
          {showRenewal && maleTotalPepmRate && (
            <RenewalRateCell
              slotKey={slotKeyOf(maleTotalPepmRate)}
              renewal={renewal}
              rowLabel="Male"
              isLastRow
            />
          )}
          {showRenewal && femaleTotalPepmRate && (
            <RenewalRateCell
              slotKey={slotKeyOf(femaleTotalPepmRate)}
              renewal={renewal}
              rowLabel="Female"
              isLastRow
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Renderer for Standard PEPM rate design (dental, vision, EAP, STD)
 *
 * Displays:
 * - Number of Enrolled (editable) → stored as rate.numberOfLives on totalPepm rate
 * - PEPM (editable) → stored as rate.rate on totalPepm rate
 *
 * When gender split is detected (rates have gender property):
 * - Shows Male and Female columns side by side
 * - Each gender has its own Number of Enrolled and Rate
 *
 * Updates rate on blur via PATCH API
 * Premium = rate × numberOfLives (calculated in SummaryGrid)
 */
export const StandardPepmRenderer: FC<StandardPepmRendererProps> = ({
  plan,
  axiosInstance,
  onRateSaved,
  readOnly,
}) => {
  const { data: rateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );

  const rateSchedule = rateSchedules?.[0];
  const fieldPathOf = rateFieldPathBuilder(rateSchedule);
  const rates = useMemo(() => rateSchedule?.rates || [], [rateSchedule?.rates]);

  const renewal = useMemo(
    () => getRenewalColumns(primarySectionSchedules(rateSchedules)),
    [rateSchedules],
  );

  // Detect if we have gender-split rates
  const hasGender = useMemo(() => rates.some((r) => r.gender != null), [rates]);

  // Find the totalPepm rates (single or male/female pair)
  const { totalPepmRate, maleTotalPepmRate, femaleTotalPepmRate } =
    useMemo(() => {
      if (hasGender) {
        return {
          totalPepmRate: undefined,
          maleTotalPepmRate: rates.find(
            (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "male",
          ),
          femaleTotalPepmRate: rates.find(
            (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "female",
          ),
        };
      }
      return {
        totalPepmRate: rates.find((r) => r.unit === RateUnitEnum.totalPepm),
        maleTotalPepmRate: undefined,
        femaleTotalPepmRate: undefined,
      };
    }, [rates, hasGender]);

  // Mutation hook for updating rate
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  // Generic handler for saving numberOfLives
  const handleSaveLives = useCallback(
    (rate: Rate | undefined, value: string) => {
      if (!axiosInstance || !rateSchedule?.id || !rate?.id) return;

      const numericValue = parseInt(value, 10);
      if (isNaN(numericValue)) return;

      rateMutation.mutate(
        {
          scheduleId: rateSchedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { numberOfLives: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    },
    [axiosInstance, onRateSaved, rateMutation, rateSchedule?.id, plan.id],
  );

  // Generic handler for saving rate
  const handleSaveRate = useCallback(
    (rate: Rate | undefined, value: string) => {
      if (!axiosInstance || !rateSchedule?.id || !rate?.id) return;

      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return;

      rateMutation.mutate(
        {
          scheduleId: rateSchedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { rate: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    },
    [axiosInstance, onRateSaved, rateMutation, rateSchedule?.id, plan.id],
  );

  // Single column commit handlers
  const handleNumberOfEnrolledCommit = useCallback(
    (value: string) => {
      handleSaveLives(totalPepmRate, value);
    },
    [handleSaveLives, totalPepmRate],
  );

  const handlePepmCommit = useCallback(
    (value: string) => {
      handleSaveRate(totalPepmRate, value);
    },
    [handleSaveRate, totalPepmRate],
  );

  // Gender-split commit handlers
  const handleMaleLivesCommit = useCallback(
    (value: string) => {
      handleSaveLives(maleTotalPepmRate, value);
    },
    [handleSaveLives, maleTotalPepmRate],
  );

  const handleMaleRateCommit = useCallback(
    (value: string) => {
      handleSaveRate(maleTotalPepmRate, value);
    },
    [handleSaveRate, maleTotalPepmRate],
  );

  const handleFemaleLivesCommit = useCallback(
    (value: string) => {
      handleSaveLives(femaleTotalPepmRate, value);
    },
    [handleSaveLives, femaleTotalPepmRate],
  );

  const handleFemaleRateCommit = useCallback(
    (value: string) => {
      handleSaveRate(femaleTotalPepmRate, value);
    },
    [handleSaveRate, femaleTotalPepmRate],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  // Gender-split layout
  if (hasGender) {
    return (
      <StandardPepmGenderSplit
        planId={plan.id}
        maleTotalPepmRate={maleTotalPepmRate}
        femaleTotalPepmRate={femaleTotalPepmRate}
        onMaleLivesCommit={handleMaleLivesCommit}
        onMaleRateCommit={handleMaleRateCommit}
        onFemaleLivesCommit={handleFemaleLivesCommit}
        onFemaleRateCommit={handleFemaleRateCommit}
        readOnly={readOnly}
        renewal={renewal}
        fieldPathOf={fieldPathOf}
      />
    );
  }

  // Single-column layout (no gender split)
  return (
    <StandardPepmSingleColumn
      planId={plan.id}
      totalPepmRate={totalPepmRate}
      onNumberOfEnrolledCommit={handleNumberOfEnrolledCommit}
      onPepmCommit={handlePepmCommit}
      readOnly={readOnly}
      renewal={renewal}
      fieldPathOf={fieldPathOf}
    />
  );
};
